"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, type MouseEvent as ReactMouseEvent, type CSSProperties } from "react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import type { Card } from "@/lib/api/types";
import RichDescription from "@/app/components/RichDescription";
import type { RelatedCard } from "@/app/components/RichDescription";
import type { EntityNameLink } from "@/lib/rich-links";
import type { KeywordEntry, PowerEntry, SpawnedCardEntry } from "@/lib/entity-catalogs";
import { getCardDisplayModel } from "@/lib/card-display";
import { cachedFetch } from "@/lib/fetch-cache";
import LocalizedNames from "@/app/components/LocalizedNames";
import EntityUpdateHistory from "@/app/components/EntityUpdateHistory";
import RelatedCards from "@/app/components/RelatedCards";
import EntityProse from "@/app/components/EntityProse";
import EntityPairings from "@/app/components/EntityPairings";
import EntityDraftRecs from "@/app/components/EntityDraftRecs";
import EntitySummary from "@/app/components/EntitySummary";
import type { EntitySummaryData } from "@/lib/entity-summary";
import type { DraftRecs, Pairings, RelatedGroup } from "@/lib/entity-links";
import { imageUrl, fullCardUrl, enchantedCardUrl } from "@/lib/image-url";
import EntityRunStats, { type EntityStats } from "@/app/components/EntityRunStats";
import EntityVersionSelect from "@/app/components/EntityVersionSelect";
import HoverTooltip from "@/app/components/HoverTooltip";
import { useChannel, useBetaPrefix } from "@/lib/api/prefix.client";
import BetaDiffNotice from "@/app/components/BetaDiffNotice";
import "@/app/card-revamp.css";
import { useBracketParam } from "@/lib/use-bracket-param";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Per-entity character accent for the wiki page spine (--spine).
const SPINE_COLOR: Record<string, string> = {
  ironclad: "var(--color-ironclad)",
  silent: "var(--color-silent)",
  defect: "var(--color-defect)",
  necrobinder: "var(--color-necrobinder)",
  regent: "var(--color-regent)",
  colorless: "var(--color-colorless)",
  curse: "var(--color-curse)",
  status: "var(--text-muted)"
};

// Headline figures for the infobox mini-stats block. Same endpoint EntityRunStats
// fetches, so cachedFetch dedupes it (no extra request).
interface MiniBracket {
  picks: number;
  win_rate: number;
  pick_rate: number;
  score: number | null;
  elo: number | null;
}
interface MiniStats extends MiniBracket {
  brackets?: Record<string, MiniBracket>;
}
const typeIcons: Record<string, string> = {
  Attack: "\u2694",
  Skill: "\uD83D\uDEE1",
  Power: "\u2726",
  Status: "\u25C6",
  Curse: "\u2620",
  Quest: "\u2605"
};
const keywordTooltips: Record<string, string> = {
  Exhaust: "Remove this card from your deck when played.",
  Ethereal: "If this card is in your hand at end of turn, discard it.",
  Innate: "Always appears in your opening hand.",
  Unplayable: "Cannot be played from your hand.",
  Retain: "Keep this card in your hand at end of turn.",
  Sly: "Can be played from the discard pile.",
  Eternal: "Cannot be removed from your deck."
};
function buildInteractiveWords(keywords: string[], powerData: Record<string, {
  id: string;
  name: string;
  description: string;
  type: string;
  image_url: string | null;
}>, keywordData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, glossaryData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, orbData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, bp: string): Record<string, {
  tooltip: string;
  href: string;
}> {
  const words: Record<string, {
    tooltip: string;
    href: string;
  }> = {};
  // Add keyword names (Sly, Exhaust, Ethereal, etc.)
  for (const kw of keywords) {
    const data = keywordData[kw.toLowerCase()];
    const desc = data?.description || keywordTooltips[kw] || "";
    words[kw] = {
      tooltip: desc,
      href: `${bp}/keywords/${kw.toLowerCase()}`
    };
  }
  // Add power names from [gold] tagged text (Dexterity, Thorns, Block, Strength, etc.)
  for (const [name, data] of Object.entries(powerData)) {
    words[data.name] = {
      tooltip: data.description,
      href: `${bp}/powers/${data.id.toLowerCase()}`
    };
  }
  // Add glossary terms (Block, Discard Pile, Draw Pile, Fatal, Forge, etc.)
  for (const [name, data] of Object.entries(glossaryData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/keywords/${data.id.toLowerCase()}`
      };
    }
  }
  // Add orb names (Lightning, Frost, Dark, Glass, Plasma)
  for (const [name, data] of Object.entries(orbData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/orbs/${data.id.toLowerCase()}`
      };
    }
  }
  return words;
}
function InlineTooltip(t0) {
  const $ = _c(23);
  const {
    label,
    tooltip,
    href,
    color,
    image
  } = t0;
  const [show, setShow] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShow(true);
    t2 = () => setShow(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== color) {
    t3 = {
      color
    };
    $[2] = color;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <span className="font-medium">{label}</span>;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== tooltip) {
    t5 = tooltip && <span className="text-[var(--text-muted)] ml-1">{tooltip.replace(/\[.*?\]/g, "").replace(/\n/g, " ").slice(0, 80)}</span>;
    $[6] = tooltip;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== image || $[9] !== label || $[10] !== show || $[11] !== tooltip) {
    t6 = show && tooltip && <span className="absolute z-[100] bottom-full left-0 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none text-left">{image && <img src={image} alt="" className="w-6 h-6 object-contain mb-1" crossOrigin="anonymous" />}<span className="font-semibold text-xs text-[var(--text-primary)] block">{label}</span><span className="text-[10px] text-[var(--text-secondary)] leading-relaxed block mt-1"><RichDescription text={tooltip} /></span></span>;
    $[8] = image;
    $[9] = label;
    $[10] = show;
    $[11] = tooltip;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <span className="relative inline-flex items-center cursor-pointer" onMouseEnter={t1} onMouseLeave={t2} style={t3}>{t4}{t5}{t6}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const inner = t7;
  if (href) {
    let t8;
    if ($[18] !== href || $[19] !== inner) {
      t8 = <Link href={href} className="block">{inner}</Link>;
      $[18] = href;
      $[19] = inner;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  let t8;
  if ($[21] !== inner) {
    t8 = <div>{inner}</div>;
    $[21] = inner;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
const energyIconMap: Record<string, string> = {
  ironclad: "ironclad",
  silent: "silent",
  defect: "defect",
  necrobinder: "necrobinder",
  regent: "regent",
  colorless: "colorless"
};

// Merchant price ranges
function getMerchantPriceRange(rarity: string, color: string): {
  min: number;
  max: number;
} | null {
  const isColorless = color === "colorless";
  let base: number;
  switch (rarity) {
    case "Common":
      base = 50;
      break;
    case "Uncommon":
      base = 75;
      break;
    case "Rare":
      base = 150;
      break;
    default:
      return null;
  }
  if (isColorless) base = Math.round(base * 1.15);
  return {
    min: Math.floor(base * 0.95),
    max: Math.ceil(base * 1.05)
  };
}
export interface CardCatalogs {
  keywords: KeywordEntry[];
  powers: PowerEntry[];
  glossary: KeywordEntry[];
  orbs: KeywordEntry[];
  spawnedCards: SpawnedCardEntry[];
}
export default function CardDetail(t0) {
  const $ = _c(469);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialCard,
    initialEnchantments,
    initialStats,
    initialPairings,
    initialRecs,
    initialRelated,
    summary: t2,
    initialCatalogs,
    initialEntityLinks
  } = t1;
  const summary = t2 === undefined ? null : t2;
  const params = useParams();
  const id = params.id as string;
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  const channel = useChannel();
  const [card, setCard] = useState(initialCard ?? null);
  let t3;
  if ($[2] !== initialCatalogs?.spawnedCards) {
    t3 = initialCatalogs?.spawnedCards ?? [];
    $[2] = initialCatalogs?.spawnedCards;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [spawnedCards, setSpawnedCards] = useState(t3);
  const [loading, setLoading] = useState(!initialCard);
  const [notFound, setNotFound] = useState(false);
  const [upgraded, setUpgraded] = useState(false);
  const [betaArt] = useState(false);
  const [cardImgFailed, setCardImgFailed] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedEnch, setSelectedEnch] = useState("none");
  const [activeSection, setActiveSection] = useState("performance");
  const [miniStats, setMiniStats] = useState(null);
  const [statsBracket, setStatsBracket] = useBracketParam();
  const byName = _temp;
  let t4;
  if ($[4] !== initialCatalogs?.powers) {
    t4 = () => byName(initialCatalogs?.powers ?? []);
    $[4] = initialCatalogs?.powers;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [powerData, setPowerData] = useState(t4);
  let t5;
  if ($[6] !== initialCatalogs?.keywords) {
    t5 = () => byName(initialCatalogs?.keywords ?? []);
    $[6] = initialCatalogs?.keywords;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [keywordData, setKeywordData] = useState(t5);
  let t6;
  if ($[8] !== initialCatalogs?.glossary) {
    t6 = () => byName(initialCatalogs?.glossary ?? []);
    $[8] = initialCatalogs?.glossary;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [glossaryData, setGlossaryData] = useState(t6);
  let t7;
  if ($[10] !== initialCatalogs?.orbs) {
    t7 = () => byName(initialCatalogs?.orbs ?? []);
    $[10] = initialCatalogs?.orbs;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const [orbData, setOrbData] = useState(t7);
  let t8;
  if ($[12] !== initialEnchantments) {
    t8 = initialEnchantments ?? [];
    $[12] = initialEnchantments;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const cardEnchantments = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {};
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const [enchMeta, setEnchMeta] = useState(t9);
  let t10;
  if ($[15] !== id || $[16] !== initialCard || $[17] !== initialCatalogs || $[18] !== lang) {
    t10 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/cards/${id}?lang=${lang}`).then(data => {
        setCard(data);
        if (!initialCatalogs && data.spawns_cards && data.spawns_cards.length > 0) {
          Promise.all(data.spawns_cards.map(sid => cachedFetch(`${API}/api/cards/${sid}?lang=${lang}`).catch(_temp2))).then(results => setSpawnedCards(results.filter(Boolean) as Card[]));
        }
      }).catch(() => {
        if (!initialCard) {
          setNotFound(true);
        }
      }).finally(() => setLoading(false));
    };
    $[15] = id;
    $[16] = initialCard;
    $[17] = initialCatalogs;
    $[18] = lang;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== id || $[21] !== initialCatalogs || $[22] !== lang) {
    t11 = [id, lang, initialCatalogs];
    $[20] = id;
    $[21] = initialCatalogs;
    $[22] = lang;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  if ($[24] !== cardEnchantments.length || $[25] !== initialCatalogs || $[26] !== lang) {
    t12 = () => {
      if (initialCatalogs) {
        return;
      }
      cachedFetch(`${API}/api/powers?lang=${lang}`).then(powers => {
        const m = {};
        for (const p of powers) {
          m[p.name.toLowerCase()] = p;
        }
        setPowerData(m);
      });
      cachedFetch(`${API}/api/keywords?lang=${lang}`).then(kws => {
        const m_0 = {};
        for (const k of kws) {
          m_0[k.name.toLowerCase()] = k;
        }
        setKeywordData(m_0);
      });
      cachedFetch(`${API}/api/glossary?lang=${lang}`).then(terms => {
        const m_1 = {};
        for (const t_0 of terms) {
          m_1[t_0.name.toLowerCase()] = t_0;
        }
        setGlossaryData(m_1);
      });
      cachedFetch(`${API}/api/orbs?lang=${lang}`).then(orbs => {
        const m_2 = {};
        for (const o of orbs) {
          m_2[o.name.toLowerCase()] = o;
        }
        setOrbData(m_2);
      });
      if (cardEnchantments.length > 0) {
        cachedFetch(`${API}/api/enchantments?lang=${lang}`).then(enchs => {
          const m_3 = {};
          for (const e_0 of enchs) {
            m_3[e_0.id.toLowerCase()] = e_0;
          }
          setEnchMeta(m_3);
        });
      }
    };
    $[24] = cardEnchantments.length;
    $[25] = initialCatalogs;
    $[26] = lang;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== initialCatalogs || $[29] !== lang) {
    t13 = [lang, initialCatalogs];
    $[28] = initialCatalogs;
    $[29] = lang;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[31] !== id) {
    t14 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/runs/stats/cards/${id}`).then(setMiniStats).catch(_temp3);
    };
    t15 = [id];
    $[31] = id;
    $[32] = t14;
    $[33] = t15;
  } else {
    t14 = $[32];
    t15 = $[33];
  }
  useEffect(t14, t15);
  let t16;
  if ($[34] !== card) {
    t16 = () => {
      if (!card) {
        return;
      }
      const secs = Array.from(document.querySelectorAll(".card-rvmp section[id]"));
      if (secs.length === 0) {
        return;
      }
      const obs = new IntersectionObserver(entries_0 => {
        entries_0.forEach(e_1 => {
          if (e_1.isIntersecting) {
            setActiveSection((e_1.target as HTMLElement).id);
          }
        });
      }, {
        rootMargin: "-130px 0px -70% 0px"
      });
      secs.forEach(s => obs.observe(s));
      return () => obs.disconnect();
    };
    $[34] = card;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== card || $[37] !== cardEnchantments.length) {
    t17 = [card, cardEnchantments.length];
    $[36] = card;
    $[37] = cardEnchantments.length;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  useEffect(t16, t17);
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (e_2, secId) => {
      e_2.preventDefault();
      const el = document.getElementById(secId);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
        setActiveSection(secId);
      }
    };
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const handleTocClick = t18;
  if (loading) {
    let t19;
    if ($[40] !== t) {
      t19 = t("Loading...");
      $[40] = t;
      $[41] = t19;
    } else {
      t19 = $[41];
    }
    let t20;
    if ($[42] !== t19) {
      t20 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="text-center py-12 text-[var(--text-muted)]">{t19}</div></div>;
      $[42] = t19;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    return t20;
  }
  if (notFound || !card) {
    const t19 = `${bp}/cards`;
    let t20;
    if ($[44] !== t) {
      t20 = t("Back to");
      $[44] = t;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== t) {
      t21 = t("Cards");
      $[46] = t;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    let t22;
    if ($[48] !== t19 || $[49] !== t20 || $[50] !== t21) {
      t22 = <Link href={t19} className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6">← {t20} {t21}</Link>;
      $[48] = t19;
      $[49] = t20;
      $[50] = t21;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== t) {
      t23 = t("Card Not Found");
      $[52] = t;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== t23) {
      t24 = <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t23}</h1>;
      $[54] = t23;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    let t25;
    if ($[56] !== id || $[57] !== t) {
      t25 = t("No card exists with ID \u201C{id}\u201D.", {
        id
      });
      $[56] = id;
      $[57] = t;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    let t26;
    if ($[59] !== t25) {
      t26 = <p className="text-[var(--text-muted)]">{t25}</p>;
      $[59] = t25;
      $[60] = t26;
    } else {
      t26 = $[60];
    }
    let t27;
    if ($[61] !== t24 || $[62] !== t26) {
      t27 = <div className="text-center py-12">{t24}{t26}</div>;
      $[61] = t24;
      $[62] = t26;
      $[63] = t27;
    } else {
      t27 = $[63];
    }
    let t28;
    if ($[64] !== t22 || $[65] !== t27) {
      t28 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t22}{t27}</div>;
      $[64] = t22;
      $[65] = t27;
      $[66] = t28;
    } else {
      t28 = $[66];
    }
    return t28;
  }
  let characterLabel;
  let costLabel;
  let displayKeywords;
  let displayType;
  let enchActive;
  let isUpgraded;
  let priceRange;
  let renderSrc;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let targetLabel;
  let variantImg;
  if ($[67] !== activeSection || $[68] !== betaArt || $[69] !== bp || $[70] !== card || $[71] !== cardEnchantments || $[72] !== cardImgFailed || $[73] !== channel || $[74] !== glossaryData || $[75] !== id || $[76] !== initialEntityLinks || $[77] !== initialStats || $[78] !== keywordData || $[79] !== lang || $[80] !== orbData || $[81] !== powerData || $[82] !== selectedEnch || $[83] !== selectedVariant || $[84] !== setStatsBracket || $[85] !== spawnedCards || $[86] !== statsBracket || $[87] !== summary || $[88] !== t || $[89] !== upgraded) {
    const display = getCardDisplayModel(card, upgraded);
    const activeVariant = selectedVariant && card.type_variants ? card.type_variants[selectedVariant] : null;
    activeVariant ? activeVariant.damage : display.damage;
    activeVariant ? activeVariant.block : display.block;
    activeVariant ? card.hit_count : display.hitCount;
    const cost = activeVariant ? card.cost : display.cost;
    displayType = activeVariant ? activeVariant.type : card.type;
    isUpgraded = display.isUpgraded;
    const hasUpgrade = !!card.upgrade;
    const hasVariants = !!card.type_variants;
    variantImg = activeVariant?.image_url || null;
    const imgUrl = variantImg ? variantImg : betaArt && card.beta_image_url ? card.beta_image_url : card.image_url || card.beta_image_url;
    const descText = activeVariant ? activeVariant.description : display.descriptionText;
    const keywordText = activeVariant ? "" : display.keywordText;
    const energyIcon = energyIconMap[card.color] || "colorless";
    const t33 = card.rarity_key || card.rarity;
    let t34;
    if ($[114] !== card.color || $[115] !== t33) {
      t34 = getMerchantPriceRange(t33, card.color);
      $[114] = card.color;
      $[115] = t33;
      $[116] = t34;
    } else {
      t34 = $[116];
    }
    priceRange = t34;
    displayKeywords = [...display.visibleKeywords, ...display.addedKeywords];
    const interactiveWords = buildInteractiveWords(displayKeywords, powerData, keywordData, glossaryData, orbData, bp);
    const spineColor = SPINE_COLOR[card.color] ?? "var(--accent-gold)";
    let t35;
    if ($[117] !== card.color) {
      t35 = card.color ? card.color.charAt(0).toUpperCase() + card.color.slice(1) : "\u2014";
      $[117] = card.color;
      $[118] = t35;
    } else {
      t35 = $[118];
    }
    characterLabel = t35;
    costLabel = card.is_x_cost ? "X" : cost != null && cost < 0 ? "U" : String(cost);
    let t36;
    if ($[119] !== card.target) {
      t36 = card.target && card.target !== "None" && card.target !== "Self" ? card.target.replace(/([A-Z])/g, " $1").trim() : null;
      $[119] = card.target;
      $[120] = t36;
    } else {
      t36 = $[120];
    }
    targetLabel = t36;
    let t37;
    if ($[121] !== cardEnchantments || $[122] !== selectedEnch) {
      t37 = selectedEnch !== "none" && cardEnchantments.includes(selectedEnch);
      $[121] = cardEnchantments;
      $[122] = selectedEnch;
      $[123] = t37;
    } else {
      t37 = $[123];
    }
    enchActive = t37;
    renderSrc = enchActive ? enchantedCardUrl(card.id.toLowerCase(), selectedEnch, isUpgraded, channel, lang) : betaArt || variantImg || cardImgFailed ? imageUrl(imgUrl) : fullCardUrl(card.id.toLowerCase(), isUpgraded, channel, lang);
    let t38;
    if ($[124] !== t) {
      t38 = t("Community");
      $[124] = t;
      $[125] = t38;
    } else {
      t38 = $[125];
    }
    let t39;
    if ($[126] !== t38) {
      t39 = {
        id: "performance",
        label: t38
      };
      $[126] = t38;
      $[127] = t39;
    } else {
      t39 = $[127];
    }
    let t40;
    if ($[128] !== t) {
      t40 = t("Description");
      $[128] = t;
      $[129] = t40;
    } else {
      t40 = $[129];
    }
    let t41;
    if ($[130] !== t40) {
      t41 = {
        id: "description",
        label: t40
      };
      $[130] = t40;
      $[131] = t41;
    } else {
      t41 = $[131];
    }
    let t42;
    if ($[132] !== t) {
      t42 = t("Relations");
      $[132] = t;
      $[133] = t42;
    } else {
      t42 = $[133];
    }
    let t43;
    if ($[134] !== t42) {
      t43 = {
        id: "relations",
        label: t42
      };
      $[134] = t42;
      $[135] = t43;
    } else {
      t43 = $[135];
    }
    let t44;
    if ($[136] !== t) {
      t44 = t("Synergy");
      $[136] = t;
      $[137] = t44;
    } else {
      t44 = $[137];
    }
    let t45;
    if ($[138] !== t44) {
      t45 = {
        id: "pairings",
        label: t44
      };
      $[138] = t44;
      $[139] = t45;
    } else {
      t45 = $[139];
    }
    let t46;
    if ($[140] !== t) {
      t46 = t("Version history");
      $[140] = t;
      $[141] = t46;
    } else {
      t46 = $[141];
    }
    let t47;
    if ($[142] !== t46) {
      t47 = {
        id: "history",
        label: t46
      };
      $[142] = t46;
      $[143] = t47;
    } else {
      t47 = $[143];
    }
    let t48;
    if ($[144] !== t39 || $[145] !== t41 || $[146] !== t43 || $[147] !== t45 || $[148] !== t47) {
      t48 = [t39, t41, t43, t45, t47];
      $[144] = t39;
      $[145] = t41;
      $[146] = t43;
      $[147] = t45;
      $[148] = t47;
      $[149] = t48;
    } else {
      t48 = $[149];
    }
    const tocItems = t48;
    t30 = "card-rvmp";
    let t49;
    if ($[150] !== imgUrl) {
      t49 = imgUrl ? {
        "--entity-bg": `url("${imageUrl(imgUrl)}?bg")`
      } : {};
      $[150] = imgUrl;
      $[151] = t49;
    } else {
      t49 = $[151];
    }
    let t50;
    if ($[152] !== spineColor || $[153] !== t49) {
      t50 = {
        "--spine": spineColor,
        ...t49
      };
      $[152] = spineColor;
      $[153] = t49;
      $[154] = t50;
    } else {
      t50 = $[154];
    }
    t31 = t50 as CSSProperties;
    const t51 = `${bp}/cards`;
    let t52;
    if ($[155] !== t) {
      t52 = t("Back to");
      $[155] = t;
      $[156] = t52;
    } else {
      t52 = $[156];
    }
    let t53;
    if ($[157] !== t) {
      t53 = t("Cards");
      $[157] = t;
      $[158] = t53;
    } else {
      t53 = $[158];
    }
    let t54;
    if ($[159] !== t51 || $[160] !== t52 || $[161] !== t53) {
      t54 = <Link href={t51} className="cd-back">← {t52} {t53}</Link>;
      $[159] = t51;
      $[160] = t52;
      $[161] = t53;
      $[162] = t54;
    } else {
      t54 = $[162];
    }
    let t55;
    if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = {
        marginTop: 12
      };
      $[163] = t55;
    } else {
      t55 = $[163];
    }
    let t56;
    if ($[164] !== card.id) {
      t56 = <div style={t55}><BetaDiffNotice entityType="cards" entityId={card.id} /></div>;
      $[164] = card.id;
      $[165] = t56;
    } else {
      t56 = $[165];
    }
    if ($[166] !== t54 || $[167] !== t56) {
      t32 = <div className="cd-top">{t54}{t56}</div>;
      $[166] = t54;
      $[167] = t56;
      $[168] = t32;
    } else {
      t32 = $[168];
    }
    t29 = "wrap";
    t25 = "main";
    let t57;
    if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <span className="dot">◆</span>;
      $[169] = t57;
    } else {
      t57 = $[169];
    }
    let t58;
    if ($[170] !== characterLabel) {
      t58 = <span>{characterLabel}</span>;
      $[170] = characterLabel;
      $[171] = t58;
    } else {
      t58 = $[171];
    }
    let t59;
    if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
      t59 = <span>·</span>;
      $[172] = t59;
    } else {
      t59 = $[172];
    }
    let t60;
    if ($[173] !== card.rarity) {
      t60 = <span>{card.rarity}</span>;
      $[173] = card.rarity;
      $[174] = t60;
    } else {
      t60 = $[174];
    }
    let t61;
    if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
      t61 = <span>·</span>;
      $[175] = t61;
    } else {
      t61 = $[175];
    }
    const t62 = typeIcons[displayType] || "";
    let t63;
    if ($[176] !== displayType || $[177] !== t62) {
      t63 = <span>{t62} {displayType}</span>;
      $[176] = displayType;
      $[177] = t62;
      $[178] = t63;
    } else {
      t63 = $[178];
    }
    let t64;
    if ($[179] === Symbol.for("react.memo_cache_sentinel")) {
      t64 = <span>·</span>;
      $[179] = t64;
    } else {
      t64 = $[179];
    }
    let t65;
    if ($[180] !== t) {
      t65 = t("Energy");
      $[180] = t;
      $[181] = t65;
    } else {
      t65 = $[181];
    }
    let t66;
    if ($[182] !== costLabel || $[183] !== t65) {
      t66 = <span>{costLabel} {t65}</span>;
      $[182] = costLabel;
      $[183] = t65;
      $[184] = t66;
    } else {
      t66 = $[184];
    }
    let t67;
    if ($[185] !== card.is_x_star_cost || $[186] !== card.star_cost) {
      t67 = (card.star_cost != null || card.is_x_star_cost) && <><span>·</span><span>{card.is_x_star_cost ? "X" : card.star_cost} ★</span></>;
      $[185] = card.is_x_star_cost;
      $[186] = card.star_cost;
      $[187] = t67;
    } else {
      t67 = $[187];
    }
    let t68;
    if ($[188] !== t58 || $[189] !== t60 || $[190] !== t63 || $[191] !== t66 || $[192] !== t67) {
      t68 = <p className="eyebrow">{t57}{t58}{t59}{t60}{t61}{t63}{t64}{t66}{t67}</p>;
      $[188] = t58;
      $[189] = t60;
      $[190] = t63;
      $[191] = t66;
      $[192] = t67;
      $[193] = t68;
    } else {
      t68 = $[193];
    }
    let t69;
    if ($[194] !== hasUpgrade || $[195] !== t || $[196] !== upgraded) {
      t69 = hasUpgrade && <div className="seg hero-seg" role="group" aria-label={t("Card version")}><button type="button" className={`segbtn${!upgraded ? " on" : ""}`} onClick={() => setUpgraded(false)}>{t("Normal")}</button><button type="button" className={`segbtn${upgraded ? " on" : ""}`} onClick={() => setUpgraded(true)}>{t("Upgraded")}</button></div>;
      $[194] = hasUpgrade;
      $[195] = t;
      $[196] = upgraded;
      $[197] = t69;
    } else {
      t69 = $[197];
    }
    const t70 = <div className="hero-head"><h1>{card.name}{isUpgraded && <span className="up">+</span>}</h1>{t69}</div>;
    let t71;
    if ($[198] !== card || $[199] !== upgraded) {
      t71 = <EntityProse kind="card" card={card} upgraded={upgraded} lead={true} />;
      $[198] = card;
      $[199] = upgraded;
      $[200] = t71;
    } else {
      t71 = $[200];
    }
    if ($[201] !== t68 || $[202] !== t70 || $[203] !== t71) {
      t26 = <div className="hero">{t68}{t70}{t71}</div>;
      $[201] = t68;
      $[202] = t70;
      $[203] = t71;
      $[204] = t26;
    } else {
      t26 = $[204];
    }
    let t72;
    if ($[205] !== t) {
      t72 = t("On this page");
      $[205] = t;
      $[206] = t72;
    } else {
      t72 = $[206];
    }
    let t73;
    if ($[207] !== t) {
      t73 = t("On this page");
      $[207] = t;
      $[208] = t73;
    } else {
      t73 = $[208];
    }
    let t74;
    if ($[209] !== t73) {
      t74 = <span className="toc-label">{t73}</span>;
      $[209] = t73;
      $[210] = t74;
    } else {
      t74 = $[210];
    }
    let t75;
    if ($[211] !== activeSection || $[212] !== tocItems) {
      t75 = tocItems.map(it => <a key={it.id} href={`#${it.id}`} className={activeSection === it.id ? "on" : undefined} onClick={e_3 => handleTocClick(e_3, it.id)}>{it.label}</a>);
      $[211] = activeSection;
      $[212] = tocItems;
      $[213] = t75;
    } else {
      t75 = $[213];
    }
    if ($[214] !== t72 || $[215] !== t74 || $[216] !== t75) {
      t27 = <nav className="toc" aria-label={t72}>{t74}{t75}</nav>;
      $[214] = t72;
      $[215] = t74;
      $[216] = t75;
      $[217] = t27;
    } else {
      t27 = $[217];
    }
    let t76;
    if ($[218] !== t) {
      t76 = t("Community performance");
      $[218] = t;
      $[219] = t76;
    } else {
      t76 = $[219];
    }
    let t77;
    if ($[220] !== t76) {
      t77 = <h2>{t76}</h2>;
      $[220] = t76;
      $[221] = t77;
    } else {
      t77 = $[221];
    }
    let t78;
    if ($[222] !== t) {
      t78 = t("Live aggregate across community-submitted runs. Filter by bracket to see how it holds up at higher levels of play.");
      $[222] = t;
      $[223] = t78;
    } else {
      t78 = $[223];
    }
    let t79;
    if ($[224] !== t78) {
      t79 = <p className="h-note">{t78}</p>;
      $[224] = t78;
      $[225] = t79;
    } else {
      t79 = $[225];
    }
    let t80;
    if ($[226] !== card.name || $[227] !== id || $[228] !== initialStats || $[229] !== setStatsBracket || $[230] !== statsBracket) {
      t80 = <EntityRunStats entityType="cards" entityId={id} entityName={card.name} variant="wiki" initialStats={initialStats} bracket={statsBracket} onBracketChange={setStatsBracket} />;
      $[226] = card.name;
      $[227] = id;
      $[228] = initialStats;
      $[229] = setStatsBracket;
      $[230] = statsBracket;
      $[231] = t80;
    } else {
      t80 = $[231];
    }
    if ($[232] !== t77 || $[233] !== t79 || $[234] !== t80) {
      t28 = <section id="performance">{t77}{t79}{t80}</section>;
      $[232] = t77;
      $[233] = t79;
      $[234] = t80;
      $[235] = t28;
    } else {
      t28 = $[235];
    }
    t19 = "description";
    let t81;
    if ($[236] !== t) {
      t81 = t("Description");
      $[236] = t;
      $[237] = t81;
    } else {
      t81 = $[237];
    }
    if ($[238] !== t81) {
      t20 = <h2>{t81}</h2>;
      $[238] = t81;
      $[239] = t20;
    } else {
      t20 = $[239];
    }
    if ($[240] !== card.name || $[241] !== summary) {
      t21 = <EntitySummary name={card.name} data={summary} />;
      $[240] = card.name;
      $[241] = summary;
      $[242] = t21;
    } else {
      t21 = $[242];
    }
    if ($[243] !== card.type || $[244] !== card.type_variants || $[245] !== hasVariants || $[246] !== selectedVariant) {
      t22 = hasVariants && card.type_variants && <div className="vtoggle">{Object.entries(card.type_variants).map(t82 => {
          const [key, v] = t82;
          const on = selectedVariant === key || !selectedVariant && key === card.type.toLowerCase();
          return <button key={key} type="button" className={`vbtn${on ? " on" : ""}`} onClick={() => setSelectedVariant(selectedVariant === key ? null : key)}>{typeIcons[v.type] || ""} {v.type}</button>;
        })}</div>;
      $[243] = card.type;
      $[244] = card.type_variants;
      $[245] = hasVariants;
      $[246] = selectedVariant;
      $[247] = t22;
    } else {
      t22 = $[247];
    }
    t23 = hasVariants && card.type_variants ? <div className="space-y-3">{Object.entries(card.type_variants).map(t82 => {
        const [key_0, v_0] = t82;
        const isActive = selectedVariant === key_0 || !selectedVariant && key_0 === card.type.toLowerCase();
        return <div key={key_0} className={`text-sm leading-relaxed rounded-lg border transition-colors overflow-hidden ${isActive ? "border-[var(--accent-gold)]/30" : "border-[var(--border-subtle)] opacity-60"}`}><div className={`px-3 py-2 ${isActive ? "bg-[var(--accent-gold)]/5" : "bg-[var(--bg-primary)]/50"}`}><span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mr-2">{typeIcons[v_0.type] || ""} {v_0.type}</span>{v_0.description ? <span className="text-[var(--text-secondary)]"><RichDescription text={v_0.description} energyIcon={energyIcon} /></span> : null}</div>{v_0.riders && v_0.riders.length > 0 && <div className="border-t border-[var(--border-subtle)] px-3 py-2 space-y-1.5">{v_0.riders.map(r => <div key={r.id} className="flex items-start gap-2 text-xs"><span className="font-medium text-[var(--accent-gold)] whitespace-nowrap flex-shrink-0">+ {r.name}</span><span className="text-[var(--text-secondary)]"><RichDescription text={r.description} energyIcon={energyIcon} /></span></div>)}</div>}</div>;
      })}</div> : <><div className="desc-quote"><RichDescription text={descText} energyIcon={energyIcon} relatedCards={spawnedCards} interactiveWords={interactiveWords} entityLinks={initialEntityLinks} /></div>{keywordText && <div className="desc-body"><RichDescription text={keywordText} energyIcon={energyIcon} interactiveWords={interactiveWords} entityLinks={initialEntityLinks} /></div>}</>;
    t24 = displayKeywords.length > 0 && <div className="kw-row">{displayKeywords.map(kw => {
        const data_0 = keywordData[kw.toLowerCase()];
        const tip = data_0?.description || keywordTooltips[kw] || "";
        return <HoverTooltip key={kw} title={data_0?.name || kw} content={tip}><Link href={`${bp}/keywords/${(data_0?.id || kw).toLowerCase()}`} className="kw">{data_0?.name || kw}</Link></HoverTooltip>;
      })}</div>;
    $[67] = activeSection;
    $[68] = betaArt;
    $[69] = bp;
    $[70] = card;
    $[71] = cardEnchantments;
    $[72] = cardImgFailed;
    $[73] = channel;
    $[74] = glossaryData;
    $[75] = id;
    $[76] = initialEntityLinks;
    $[77] = initialStats;
    $[78] = keywordData;
    $[79] = lang;
    $[80] = orbData;
    $[81] = powerData;
    $[82] = selectedEnch;
    $[83] = selectedVariant;
    $[84] = setStatsBracket;
    $[85] = spawnedCards;
    $[86] = statsBracket;
    $[87] = summary;
    $[88] = t;
    $[89] = upgraded;
    $[90] = characterLabel;
    $[91] = costLabel;
    $[92] = displayKeywords;
    $[93] = displayType;
    $[94] = enchActive;
    $[95] = isUpgraded;
    $[96] = priceRange;
    $[97] = renderSrc;
    $[98] = t19;
    $[99] = t20;
    $[100] = t21;
    $[101] = t22;
    $[102] = t23;
    $[103] = t24;
    $[104] = t25;
    $[105] = t26;
    $[106] = t27;
    $[107] = t28;
    $[108] = t29;
    $[109] = t30;
    $[110] = t31;
    $[111] = t32;
    $[112] = targetLabel;
    $[113] = variantImg;
  } else {
    characterLabel = $[90];
    costLabel = $[91];
    displayKeywords = $[92];
    displayType = $[93];
    enchActive = $[94];
    isUpgraded = $[95];
    priceRange = $[96];
    renderSrc = $[97];
    t19 = $[98];
    t20 = $[99];
    t21 = $[100];
    t22 = $[101];
    t23 = $[102];
    t24 = $[103];
    t25 = $[104];
    t26 = $[105];
    t27 = $[106];
    t28 = $[107];
    t29 = $[108];
    t30 = $[109];
    t31 = $[110];
    t32 = $[111];
    targetLabel = $[112];
    variantImg = $[113];
  }
  let t33;
  if ($[248] !== bp || $[249] !== card.sources || $[250] !== t) {
    t33 = card.sources && card.sources.length > 0 && <><h3 className="subh">{t("Sources")}</h3><p className="h-note">{t("Added to your deck in combat by:")}</p><div className="kw-row">{card.sources.map(s_0 => <Link key={s_0.id} href={`${bp}/monsters/${s_0.id.toLowerCase()}`} className="kw">{s_0.name}</Link>)}</div></>;
    $[248] = bp;
    $[249] = card.sources;
    $[250] = t;
    $[251] = t33;
  } else {
    t33 = $[251];
  }
  let t34;
  if ($[252] !== bp || $[253] !== card.powers_applied || $[254] !== powerData || $[255] !== t) {
    t34 = card.powers_applied && card.powers_applied.length > 0 && <><h3 className="subh">{t("Powers Applied")}</h3><div className="pow-list">{card.powers_applied.map(pa => {
          const powerName = pa.power_key || pa.power;
          const powerId = powerName.replace(/([A-Z])/g, "_$1").replace(/^_/, "").toUpperCase();
          const prettyName = pa.power.replace(/([A-Z])/g, " $1").trim();
          const data_1 = powerData[pa.power.toLowerCase()];
          return <HoverTooltip key={pa.power} title={prettyName} content={data_1?.description}><Link href={`${bp}/powers/${powerId}`}>{prettyName}{pa.amount ? ` ${pa.amount}` : ""}</Link></HoverTooltip>;
        })}</div></>;
    $[252] = bp;
    $[253] = card.powers_applied;
    $[254] = powerData;
    $[255] = t;
    $[256] = t34;
  } else {
    t34 = $[256];
  }
  let t35;
  if ($[257] !== t19 || $[258] !== t20 || $[259] !== t21 || $[260] !== t22 || $[261] !== t23 || $[262] !== t24 || $[263] !== t33 || $[264] !== t34) {
    t35 = <section id={t19}>{t20}{t21}{t22}{t23}{t24}{t33}{t34}</section>;
    $[257] = t19;
    $[258] = t20;
    $[259] = t21;
    $[260] = t22;
    $[261] = t23;
    $[262] = t24;
    $[263] = t33;
    $[264] = t34;
    $[265] = t35;
  } else {
    t35 = $[265];
  }
  let t36;
  if ($[266] !== t) {
    t36 = t("Relations");
    $[266] = t;
    $[267] = t36;
  } else {
    t36 = $[267];
  }
  let t37;
  if ($[268] !== t36) {
    t37 = <h2>{t36}</h2>;
    $[268] = t36;
    $[269] = t37;
  } else {
    t37 = $[269];
  }
  let t38;
  if ($[270] !== t) {
    t38 = t("What this card makes, and what else interacts with it.");
    $[270] = t;
    $[271] = t38;
  } else {
    t38 = $[271];
  }
  let t39;
  if ($[272] !== t38) {
    t39 = <p className="h-note">{t38}</p>;
    $[272] = t38;
    $[273] = t39;
  } else {
    t39 = $[273];
  }
  let t40;
  if ($[274] !== bp || $[275] !== spawnedCards || $[276] !== t) {
    t40 = spawnedCards.length > 0 && <div className="rel-block"><div className="rl">{t("Generates")}</div><div className="chips">{spawnedCards.map(sc => <Link key={sc.id} href={`${bp}/cards/${sc.id.toLowerCase()}`} className="cardlink">{sc.image_url && <img className="cardimg xs" src={imageUrl(sc.image_url)} alt="" crossOrigin="anonymous" />}<span><span className="cln">{sc.name}</span><span className="cls">{sc.type}</span></span></Link>)}</div></div>;
    $[274] = bp;
    $[275] = spawnedCards;
    $[276] = t;
    $[277] = t40;
  } else {
    t40 = $[277];
  }
  let t41;
  if ($[278] !== card.color || $[279] !== card.tags || $[280] !== displayKeywords || $[281] !== id || $[282] !== initialRelated) {
    t41 = <RelatedCards currentId={id} keywords={displayKeywords} tags={card.tags} color={card.color} initialGroups={initialRelated} />;
    $[278] = card.color;
    $[279] = card.tags;
    $[280] = displayKeywords;
    $[281] = id;
    $[282] = initialRelated;
    $[283] = t41;
  } else {
    t41 = $[283];
  }
  let t42;
  if ($[284] !== t40 || $[285] !== t41) {
    t42 = <div className="rel">{t40}{t41}</div>;
    $[284] = t40;
    $[285] = t41;
    $[286] = t42;
  } else {
    t42 = $[286];
  }
  let t43;
  if ($[287] !== t37 || $[288] !== t39 || $[289] !== t42) {
    t43 = <section id="relations">{t37}{t39}{t42}</section>;
    $[287] = t37;
    $[288] = t39;
    $[289] = t42;
    $[290] = t43;
  } else {
    t43 = $[290];
  }
  let t44;
  if ($[291] !== bp || $[292] !== card.name || $[293] !== id || $[294] !== initialPairings || $[295] !== lang) {
    t44 = <EntityPairings kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialPairings} />;
    $[291] = bp;
    $[292] = card.name;
    $[293] = id;
    $[294] = initialPairings;
    $[295] = lang;
    $[296] = t44;
  } else {
    t44 = $[296];
  }
  let t45;
  if ($[297] !== bp || $[298] !== card.name || $[299] !== id || $[300] !== initialRecs || $[301] !== lang) {
    t45 = <EntityDraftRecs kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialRecs} />;
    $[297] = bp;
    $[298] = card.name;
    $[299] = id;
    $[300] = initialRecs;
    $[301] = lang;
    $[302] = t45;
  } else {
    t45 = $[302];
  }
  let t46;
  let t47;
  if ($[303] !== id) {
    t46 = <LocalizedNames entityType="cards" entityId={id} />;
    t47 = <EntityUpdateHistory entityType="cards" entityId={id} />;
    $[303] = id;
    $[304] = t46;
    $[305] = t47;
  } else {
    t46 = $[304];
    t47 = $[305];
  }
  let t48;
  if ($[306] !== t25 || $[307] !== t26 || $[308] !== t27 || $[309] !== t28 || $[310] !== t35 || $[311] !== t43 || $[312] !== t44 || $[313] !== t45 || $[314] !== t46 || $[315] !== t47) {
    t48 = <main className={t25}>{t26}{t27}{t28}{t35}{t43}{t44}{t45}{t46}{t47}</main>;
    $[306] = t25;
    $[307] = t26;
    $[308] = t27;
    $[309] = t28;
    $[310] = t35;
    $[311] = t43;
    $[312] = t44;
    $[313] = t45;
    $[314] = t46;
    $[315] = t47;
    $[316] = t48;
  } else {
    t48 = $[316];
  }
  const t49 = `${card.name}${isUpgraded ? "+" : ""}`;
  let t50;
  if ($[317] !== t || $[318] !== t49) {
    t50 = t("{name} - Slay the Spire 2", {
      name: t49
    });
    $[317] = t;
    $[318] = t49;
    $[319] = t50;
  } else {
    t50 = $[319];
  }
  let t51;
  if ($[320] !== betaArt || $[321] !== card.id || $[322] !== channel || $[323] !== enchActive || $[324] !== lang || $[325] !== selectedEnch || $[326] !== variantImg) {
    t51 = e_4 => {
      const el_0 = e_4.currentTarget;
      if (enchActive) {
        if (!el_0.dataset.fb) {
          el_0.dataset.fb = "1";
          el_0.src = enchantedCardUrl(card.id.toLowerCase(), selectedEnch, false, channel, lang);
        }
      } else {
        if (!betaArt && !variantImg) {
          setCardImgFailed(true);
        }
      }
    };
    $[320] = betaArt;
    $[321] = card.id;
    $[322] = channel;
    $[323] = enchActive;
    $[324] = lang;
    $[325] = selectedEnch;
    $[326] = variantImg;
    $[327] = t51;
  } else {
    t51 = $[327];
  }
  let t52;
  if ($[328] !== renderSrc || $[329] !== t50 || $[330] !== t51) {
    t52 = <img key={renderSrc} className="cardimg render cardframe" src={renderSrc} alt={t50} crossOrigin="anonymous" onError={t51} />;
    $[328] = renderSrc;
    $[329] = t50;
    $[330] = t51;
    $[331] = t52;
  } else {
    t52 = $[331];
  }
  let t53;
  if ($[332] !== cardEnchantments || $[333] !== enchMeta || $[334] !== selectedEnch || $[335] !== t) {
    t53 = cardEnchantments.length > 0 && <select className="ench-select" aria-label={t("Enchantment")} value={selectedEnch} onChange={e_5 => setSelectedEnch(e_5.target.value)}><option value="none">{t("No enchantment")}</option>{cardEnchantments.map(eid => <option key={eid} value={eid}>{enchMeta[eid]?.name ?? eid}</option>)}</select>;
    $[332] = cardEnchantments;
    $[333] = enchMeta;
    $[334] = selectedEnch;
    $[335] = t;
    $[336] = t53;
  } else {
    t53 = $[336];
  }
  let t54;
  if ($[337] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = d => {
      if (d) {
        setCard(d as Card);
      }
    };
    $[337] = t54;
  } else {
    t54 = $[337];
  }
  let t55;
  if ($[338] !== id || $[339] !== setStatsBracket || $[340] !== statsBracket) {
    t55 = <EntityVersionSelect entityType="cards" entityId={id} bracket={statsBracket} onBracketChange={setStatsBracket} onEntityData={t54} />;
    $[338] = id;
    $[339] = setStatsBracket;
    $[340] = statsBracket;
    $[341] = t55;
  } else {
    t55 = $[341];
  }
  let t56;
  if ($[342] !== t53 || $[343] !== t55) {
    t56 = <div className="variant">{t53}{t55}</div>;
    $[342] = t53;
    $[343] = t55;
    $[344] = t56;
  } else {
    t56 = $[344];
  }
  let t57;
  if ($[345] !== t) {
    t57 = t("At a glance");
    $[345] = t;
    $[346] = t57;
  } else {
    t57 = $[346];
  }
  let t58;
  if ($[347] !== t57) {
    t58 = <div className="fh">{t57}</div>;
    $[347] = t57;
    $[348] = t58;
  } else {
    t58 = $[348];
  }
  let t59;
  if ($[349] !== t) {
    t59 = t("Energy");
    $[349] = t;
    $[350] = t59;
  } else {
    t59 = $[350];
  }
  let t60;
  if ($[351] !== t59) {
    t60 = <dt>{t59}</dt>;
    $[351] = t59;
    $[352] = t60;
  } else {
    t60 = $[352];
  }
  let t61;
  if ($[353] !== costLabel) {
    t61 = <dd>{costLabel}</dd>;
    $[353] = costLabel;
    $[354] = t61;
  } else {
    t61 = $[354];
  }
  let t62;
  if ($[355] !== t60 || $[356] !== t61) {
    t62 = <div className="frow">{t60}{t61}</div>;
    $[355] = t60;
    $[356] = t61;
    $[357] = t62;
  } else {
    t62 = $[357];
  }
  let t63;
  if ($[358] !== card.is_x_star_cost || $[359] !== card.star_cost || $[360] !== t) {
    t63 = (card.star_cost != null || card.is_x_star_cost) && <div className="frow"><dt>{t("Star Cost")}</dt><dd>{card.is_x_star_cost ? "X" : card.star_cost}</dd></div>;
    $[358] = card.is_x_star_cost;
    $[359] = card.star_cost;
    $[360] = t;
    $[361] = t63;
  } else {
    t63 = $[361];
  }
  let t64;
  if ($[362] !== t) {
    t64 = t("Type");
    $[362] = t;
    $[363] = t64;
  } else {
    t64 = $[363];
  }
  let t65;
  if ($[364] !== t64) {
    t65 = <dt>{t64}</dt>;
    $[364] = t64;
    $[365] = t65;
  } else {
    t65 = $[365];
  }
  let t66;
  if ($[366] !== displayType) {
    t66 = <dd>{displayType}</dd>;
    $[366] = displayType;
    $[367] = t66;
  } else {
    t66 = $[367];
  }
  let t67;
  if ($[368] !== t65 || $[369] !== t66) {
    t67 = <div className="frow">{t65}{t66}</div>;
    $[368] = t65;
    $[369] = t66;
    $[370] = t67;
  } else {
    t67 = $[370];
  }
  let t68;
  if ($[371] !== t) {
    t68 = t("Rarity");
    $[371] = t;
    $[372] = t68;
  } else {
    t68 = $[372];
  }
  let t69;
  if ($[373] !== t68) {
    t69 = <dt>{t68}</dt>;
    $[373] = t68;
    $[374] = t69;
  } else {
    t69 = $[374];
  }
  let t70;
  if ($[375] !== card.rarity) {
    t70 = <dd>{card.rarity}</dd>;
    $[375] = card.rarity;
    $[376] = t70;
  } else {
    t70 = $[376];
  }
  let t71;
  if ($[377] !== t69 || $[378] !== t70) {
    t71 = <div className="frow">{t69}{t70}</div>;
    $[377] = t69;
    $[378] = t70;
    $[379] = t71;
  } else {
    t71 = $[379];
  }
  let t72;
  if ($[380] !== t) {
    t72 = t("Character");
    $[380] = t;
    $[381] = t72;
  } else {
    t72 = $[381];
  }
  let t73;
  if ($[382] !== t72) {
    t73 = <dt>{t72}</dt>;
    $[382] = t72;
    $[383] = t73;
  } else {
    t73 = $[383];
  }
  let t74;
  if ($[384] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = {
      color: "var(--spine)"
    };
    $[384] = t74;
  } else {
    t74 = $[384];
  }
  let t75;
  if ($[385] !== characterLabel) {
    t75 = <dd style={t74}>{characterLabel}</dd>;
    $[385] = characterLabel;
    $[386] = t75;
  } else {
    t75 = $[386];
  }
  let t76;
  if ($[387] !== t73 || $[388] !== t75) {
    t76 = <div className="frow">{t73}{t75}</div>;
    $[387] = t73;
    $[388] = t75;
    $[389] = t76;
  } else {
    t76 = $[389];
  }
  let t77;
  if ($[390] !== t || $[391] !== targetLabel) {
    t77 = targetLabel && <div className="frow"><dt>{t("Target")}</dt><dd>{targetLabel}</dd></div>;
    $[390] = t;
    $[391] = targetLabel;
    $[392] = t77;
  } else {
    t77 = $[392];
  }
  let t78;
  if ($[393] !== displayKeywords || $[394] !== t) {
    t78 = displayKeywords.length > 0 && <div className="frow"><dt>{t("Keywords")}</dt><dd>{displayKeywords.map(_temp4)}</dd></div>;
    $[393] = displayKeywords;
    $[394] = t;
    $[395] = t78;
  } else {
    t78 = $[395];
  }
  let t79;
  if ($[396] !== bp || $[397] !== spawnedCards || $[398] !== t) {
    t79 = spawnedCards.length > 0 && <div className="frow"><dt>{t("Generates")}</dt><dd>{spawnedCards.map((sc_0, i) => <span key={sc_0.id}>{i > 0 ? ", " : ""}<Link href={`${bp}/cards/${sc_0.id.toLowerCase()}`}>{sc_0.name}</Link></span>)}</dd></div>;
    $[396] = bp;
    $[397] = spawnedCards;
    $[398] = t;
    $[399] = t79;
  } else {
    t79 = $[399];
  }
  let t80;
  if ($[400] !== priceRange || $[401] !== t) {
    t80 = priceRange && <div className="frow"><dt>{t("Merchant Price")}</dt><dd><img src={imageUrl("/static/images/ui/rewards/reward_icon_money.webp")} alt={t("Gold")} style={{
          width: 15,
          height: 15
        }} crossOrigin="anonymous" />{priceRange.min}–{priceRange.max}</dd></div>;
    $[400] = priceRange;
    $[401] = t;
    $[402] = t80;
  } else {
    t80 = $[402];
  }
  let t81;
  if ($[403] !== t62 || $[404] !== t63 || $[405] !== t67 || $[406] !== t71 || $[407] !== t76 || $[408] !== t77 || $[409] !== t78 || $[410] !== t79 || $[411] !== t80) {
    t81 = <dl>{t62}{t63}{t67}{t71}{t76}{t77}{t78}{t79}{t80}</dl>;
    $[403] = t62;
    $[404] = t63;
    $[405] = t67;
    $[406] = t71;
    $[407] = t76;
    $[408] = t77;
    $[409] = t78;
    $[410] = t79;
    $[411] = t80;
    $[412] = t81;
  } else {
    t81 = $[412];
  }
  let t82;
  if ($[413] !== t58 || $[414] !== t81) {
    t82 = <div className="facts">{t58}{t81}</div>;
    $[413] = t58;
    $[414] = t81;
    $[415] = t82;
  } else {
    t82 = $[415];
  }
  let t83;
  bb0: {
    const mini = miniStats?.brackets?.[statsBracket] ?? miniStats;
    if (!mini || mini.picks <= 0) {
      t83 = null;
      break bb0;
    }
    let t84;
    if ($[416] !== t) {
      t84 = t("Community");
      $[416] = t;
      $[417] = t84;
    } else {
      t84 = $[417];
    }
    let t85;
    if ($[418] !== t84) {
      t85 = <div className="mh">{t84}</div>;
      $[418] = t84;
      $[419] = t85;
    } else {
      t85 = $[419];
    }
    const t86 = mini.win_rate >= 50 ? "var(--good)" : "var(--warn)";
    let t87;
    if ($[420] !== t86) {
      t87 = {
        color: t86
      };
      $[420] = t86;
      $[421] = t87;
    } else {
      t87 = $[421];
    }
    let t88;
    if ($[422] !== mini.win_rate || $[423] !== t87) {
      t88 = <div className="mv" style={t87}>{mini.win_rate}%</div>;
      $[422] = mini.win_rate;
      $[423] = t87;
      $[424] = t88;
    } else {
      t88 = $[424];
    }
    let t89;
    if ($[425] !== t) {
      t89 = t("Win rate");
      $[425] = t;
      $[426] = t89;
    } else {
      t89 = $[426];
    }
    let t90;
    if ($[427] !== t89) {
      t90 = <div className="ml">{t89}</div>;
      $[427] = t89;
      $[428] = t90;
    } else {
      t90 = $[428];
    }
    let t91;
    if ($[429] !== t88 || $[430] !== t90) {
      t91 = <div>{t88}{t90}</div>;
      $[429] = t88;
      $[430] = t90;
      $[431] = t91;
    } else {
      t91 = $[431];
    }
    let t92;
    if ($[432] !== mini.pick_rate) {
      t92 = <div className="mv">{mini.pick_rate}%</div>;
      $[432] = mini.pick_rate;
      $[433] = t92;
    } else {
      t92 = $[433];
    }
    let t93;
    if ($[434] !== t) {
      t93 = t("Pick rate");
      $[434] = t;
      $[435] = t93;
    } else {
      t93 = $[435];
    }
    let t94;
    if ($[436] !== t93) {
      t94 = <div className="ml">{t93}</div>;
      $[436] = t93;
      $[437] = t94;
    } else {
      t94 = $[437];
    }
    let t95;
    if ($[438] !== t92 || $[439] !== t94) {
      t95 = <div>{t92}{t94}</div>;
      $[438] = t92;
      $[439] = t94;
      $[440] = t95;
    } else {
      t95 = $[440];
    }
    let t96;
    if ($[441] !== mini.score || $[442] !== t) {
      t96 = mini.score != null && <div><div className="mv">{mini.score}</div><div className="ml">{t("Codex Score")}</div></div>;
      $[441] = mini.score;
      $[442] = t;
      $[443] = t96;
    } else {
      t96 = $[443];
    }
    let t97;
    if ($[444] !== mini.elo || $[445] !== t) {
      t97 = mini.elo != null && <div><div className="mv">{Math.round(mini.elo)}</div><div className="ml">{t("Elo")}</div></div>;
      $[444] = mini.elo;
      $[445] = t;
      $[446] = t97;
    } else {
      t97 = $[446];
    }
    let t98;
    if ($[447] !== t91 || $[448] !== t95 || $[449] !== t96 || $[450] !== t97) {
      t98 = <div className="mg">{t91}{t95}{t96}{t97}</div>;
      $[447] = t91;
      $[448] = t95;
      $[449] = t96;
      $[450] = t97;
      $[451] = t98;
    } else {
      t98 = $[451];
    }
    let t99;
    if ($[452] !== t85 || $[453] !== t98) {
      t99 = <div className="mini">{t85}{t98}</div>;
      $[452] = t85;
      $[453] = t98;
      $[454] = t99;
    } else {
      t99 = $[454];
    }
    t83 = t99;
  }
  let t84;
  if ($[455] !== t52 || $[456] !== t56 || $[457] !== t82 || $[458] !== t83) {
    t84 = <aside className="aside"><div className="box">{t52}{t56}{t82}{t83}</div></aside>;
    $[455] = t52;
    $[456] = t56;
    $[457] = t82;
    $[458] = t83;
    $[459] = t84;
  } else {
    t84 = $[459];
  }
  let t85;
  if ($[460] !== t29 || $[461] !== t48 || $[462] !== t84) {
    t85 = <div className={t29}>{t48}{t84}</div>;
    $[460] = t29;
    $[461] = t48;
    $[462] = t84;
    $[463] = t85;
  } else {
    t85 = $[463];
  }
  let t86;
  if ($[464] !== t30 || $[465] !== t31 || $[466] !== t32 || $[467] !== t85) {
    t86 = <div className={t30} style={t31}>{t32}{t85}</div>;
    $[464] = t30;
    $[465] = t31;
    $[466] = t32;
    $[467] = t85;
    $[468] = t86;
  } else {
    t86 = $[468];
  }
  return t86;
}
function _temp4(kw_0) {
  return <span className="kw" key={kw_0}>{kw_0}</span>;
}
function _temp3() {}
function _temp2() {
  return null;
}
function _temp(entries) {
  const map = {};
  for (const e of entries) {
    map[e.name.toLowerCase()] = e;
  }
  return map;
}